import React from 'react';
import { currentLocale } from '@trello/locale';
import { EnterpriseDashboardTestIds } from '@trello/webdriverio-tests';
import { Button } from 'app/common/components/controls/button/button';
import { Spinner } from 'app/src/components/Spinner';
import { PageHeader } from './PageHeader';
import { TeamLogo } from './TeamLogo';
import { Tabs, ClassicLoadingStates } from './constants';

import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import styles from './PendingTeamsPage.less';

const format = forTemplate('enterprise_pending_teams');
const formatTitle = forNamespace('view title');

interface PendingTeam {
  id: string;
  displayName: string;
  requestorName: string;
  requestDate: Date;
  logoUrl?: string;
  numMembers: number;
  newBillableMembers: PendingTeamMember[];
  restrictedMembers: PendingTeamMember[];
}

interface PendingTeamMember {
  name: string;
  avatarUrl?: string;
  username: string;
}

interface PendingTeamsPageProps {
  loading?: boolean;
  teams: PendingTeam[];
  totalCount: number;
  onApprove: (pendingTeam: PendingTeam) => void;
  onDecline: (pendingTeam: PendingTeam) => void;
}

export const PendingTeamsPage = (props: PendingTeamsPageProps) => {
  const { loading = false, teams, totalCount, onApprove, onDecline } = props;
  return (
    <>
      <PageHeader
        title={formatTitle(Tabs.PENDING_TEAMS)}
        count={teams.length}
        totalCount={totalCount}
        isFiltering={false}
        sortComponent={<button>Temporary Sort Component</button>}
        searchComponent={<button>Temporary Search Component</button>}
      />
      <hr />
      {teams.map((t) => (
        <PendingTeamItem
          {...t}
          key={t.id}
          onApprove={() => onApprove(t)}
          onDecline={() => onDecline(t)}
        />
      ))}
      {loading && <Spinner centered />}
      <hr />
    </>
  );
};

interface PendingTeamItemProps extends PendingTeam {
  onApprove: () => void;
  onDecline: () => void;
}

const PendingTeamItem = (props: PendingTeamItemProps) => {
  const {
    id,
    displayName,
    requestorName,
    requestDate,
    numMembers,
    logoUrl,
    newBillableMembers,
    restrictedMembers,
  } = props;
  return (
    <div
      className={styles.pendingTeamItem}
      data-test-id={`${EnterpriseDashboardTestIds.PendingTeamItem}${id}`}
    >
      <TeamLogo className={styles.teamAvatar} size={32} url={logoUrl} />
      <div className={styles.requestInfo}>
        <span className={styles.teamName}>{displayName}</span>
        <span className={styles.requestor}>
          {format('requested-by', {
            requestorName,
            date: requestDate.toLocaleDateString(currentLocale, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }),
          })}
        </span>
      </div>
      <div className={styles.members}>
        <span>{format('members', { numMembers })}</span>
        <span className={styles.membersDetails}>
          {/*TODO: Open newBillableMembers Popover*/}
          <a
            href="#"
            data-test-id={`${EnterpriseDashboardTestIds.NewBillableMembers}`}
          >
            {format('new-billable', {
              numNewBillable: newBillableMembers.length,
            })}
          </a>
          ,&ensp;
          {/*TODO: Open restrictedMembers Popover*/}
          <a
            href="#"
            data-test-id={`${EnterpriseDashboardTestIds.RestrictedMembers}`}
          >
            {format('restricted', {
              numRestricted: restrictedMembers.length,
            })}
          </a>
        </span>
      </div>
      {/* TODO: Open confirmatino dialogs */}
      <div className={styles.actions}>
        <Button onClick={() => {}}>{format('decline')}</Button>
        <Button onClick={() => {}}>{format('approve')}</Button>
      </div>
    </div>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
interface PendingTeamsPageAdapterProps {
  itemList: any;
  onApprove: (team: any) => void;
  onDecline: (team: any) => void;
}

/**
 * The PendingTeamsPageAdapter accepts classic-specific
 * objects (ItemLists, Backbone models, etc), and renders
 * the data-agnostic PendingTeamsPage component using
 * those objects. This component will eventually be replaced
 * when the dashboard is ready to move to GraphQL.
 */
export const PendingTeamsPageAdapter = (
  props: PendingTeamsPageAdapterProps,
) => {
  const { itemList, onApprove, onDecline } = props;
  const teams = itemList.items().map((i: any) => {
    return {
      id: i.id,
      displayName: i.displayName,
      requestorName: i.memberRequestor && i.memberRequestor.fullName,
      requestDate: new Date(i.date),
      //TODO: Add logoUrl once it's returned from server
      numMembers: i.membershipCount,
      newBillableMembers: i.transferability
        ? i.transferability.newBillableMembers
        : [],
      restrictedMembers: i.transferability
        ? i.transferability.restrictedMembers
        : [],
    };
  });

  const _onApprove = (team: PendingTeam) => {
    const originalItem = itemList.items().find((i: any) => i.id === team.id);
    if (originalItem) {
      onApprove(originalItem);
    }
  };

  const _onDecline = (team: PendingTeam) => {
    const originalItem = itemList.items().find((i: any) => i.id === team.id);
    if (originalItem) {
      onDecline(originalItem);
    }
  };

  const loading =
    itemList.loadingState === ClassicLoadingStates.LOADING_NEW ||
    itemList.loadingState === ClassicLoadingStates.LOADING_NEXT;
  return (
    <PendingTeamsPage
      loading={loading}
      teams={teams}
      totalCount={itemList.totalLength()}
      onApprove={_onApprove}
      onDecline={_onDecline}
    />
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */
